// components/layout/client/SearchAndAuthButtons.tsx
'use client';

import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import UserLineIcon from 'remixicon-react/UserLineIcon';
import LoginFormComponent from '@/components/(dashboard)/auth/LoginFormComponent';
import { Spinner } from '@/components/Spinner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import useAuthStore from '@/stores/profile/authStore';
import Link from 'next/link';
import useVerifyUser from '@/hooks/profile/useVerifyUser';

interface AuthButtonsProps {
    className?: string;
    authClass?: string;
}

export default function AuthButtons({ className, authClass }: AuthButtonsProps) {
    const [isModalLoginOpen, setIsModalLoginOpen] = useState(false);
    const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
    const setIsLoggedIn = useAuthStore((state) => state.setIsLoggedIn);
    const { isLoading, isLoggedInGlobal, user } = useVerifyUser();
    const accountTitle = user?.firstName ? `حساب کاربری ${user.firstName}` : 'حساب کاربری';
    const pathname = usePathname();
    // If you really want to skip SSR rendering this whole thing on /trade-webinar-24/,
    // you can simply return null here if pathname matches.
    if (pathname === '/trade-webinar-24/') {
        return null;
    }

    const handleLoginSuccess = () => {
        setIsLoggedIn(true);
        useAuthStore.getState().setIsLoggedIn(true);
        useAuthStore.getState().setIsAuthVerified(true);
        setIsModalLoginOpen(false);
        window.location.reload();
    };

    return (
        <div className={`flex items-center gap-x-2 overflow-hidden w-fit ${className}`}>
            {/* (3) Profile / Login */}
            {isLoading ? (
                <div className='flex items-center justify-center size-7 shrink-0'>
                    <Spinner />
                </div>
            ) : isLoggedIn || isLoggedInGlobal ? (
                <Link
                    prefetch={false}
                    href='/profile/dashboard'
                    title={accountTitle}
                    aria-label={accountTitle}
                    className={`relative flex items-center justify-center size-7 rounded-full border border-primary/15 bg-gray-50 text-primary shadow-xs transition-all hover:bg-progress hover:text-white hover:border-progress dark:border-white/30 dark:bg-white/5 dark:text-white shrink-0 ${authClass || ''}`}
                >
                    <UserLineIcon size={15} />
                    {/* Signed-in mark: a small dot instead of the name's first letter. */}
                    <span
                        aria-hidden='true'
                        className='absolute -bottom-px -end-px size-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-primary-dark'
                    />
                </Link>
            ) : (
                <Dialog open={isModalLoginOpen} onOpenChange={setIsModalLoginOpen}>
                    <DialogTrigger
                        aria-label='open login modal form'
                        className={`flex items-center justify-center size-7 rounded-full bg-primary dark:bg-progress text-white transition-all dark:hover:brightness-125 hover:bg-progress active:scale-95 shadow-xs shrink-0 ${authClass || ''}`}
                    >
                        <UserLineIcon size={15} className='text-white' />
                    </DialogTrigger>
                    <DialogContent
                        className='max-lg:w-11/12 !rounded-xl bg-white border-white/20 dark:bg-primary-dark'
                        dir='rtl'
                    >
                        <DialogHeader>
                            <DialogTitle />
                            <DialogDescription asChild>
                                <div className='flex flex-col items-center justify-center'>
                                    <LoginFormComponent handleLoginSuccess={handleLoginSuccess} />
                                    <small className='text-xs text-black/50 mt-4 dark:text-white/50'>
                                        <span className='inline-block'>
                                            {' '}
                                            ورود و ‌ثبت‌نام شما به معنای پذیرش{' '}
                                        </span>
                                        <Link
                                            prefetch={false}
                                            target='_blank'
                                            className='text-primary dark:text-white mx-1 inline-block'
                                            href='/privacy-policy'
                                        >
                                            قوانین
                                        </Link>
                                        <span className='inline-block'> خانه سرمایه است. </span>
                                    </small>
                                </div>
                            </DialogDescription>
                        </DialogHeader>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
